/**
 * AI Analysis Health Monitoring Service
 * Provides monitoring, health checks, and performance metrics for the pet image analysis API
 */

interface HealthMetrics {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  averageResponseTime: number;
  lastRequestTime: number;
  consecutiveFailures: number;
  isHealthy: boolean;
  errorRate: number;
}

interface RequestMetrics {
  startTime: number;
  endTime?: number;
  success: boolean;
  error?: string;
  retryCount: number;
}

class AIAnalysisHealthMonitor {
  private metrics: HealthMetrics = {
    totalRequests: 0,
    successfulRequests: 0,
    failedRequests: 0,
    averageResponseTime: 0,
    lastRequestTime: 0,
    consecutiveFailures: 0,
    isHealthy: true,
    errorRate: 0
  };

  private requestHistory: RequestMetrics[] = [];
  private readonly maxHistorySize = 100; // Keep last 100 requests
  private readonly unhealthyThreshold = 5; // Consider unhealthy after 5 consecutive failures
  private readonly maxErrorRate = 0.5; // 50% error rate threshold

  /**
   * Records the start of a new request
   */
  recordRequestStart(): string {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const startTime = Date.now();
    
    this.requestHistory.push({
      startTime,
      success: false,
      retryCount: 0
    });

    this.metrics.totalRequests++;
    this.metrics.lastRequestTime = startTime;

    // Trim history if it gets too large
    if (this.requestHistory.length > this.maxHistorySize) {
      this.requestHistory.shift();
    }

    return requestId;
  }

  /**
   * Records the completion of a request
   */
  recordRequestEnd(requestId: string, success: boolean, error?: string, retryCount: number = 0): void {
    const request = this.requestHistory[this.requestHistory.length - 1];
    if (!request) return;

    const endTime = Date.now();
    const responseTime = endTime - request.startTime;

    request.endTime = endTime;
    request.success = success;
    request.error = error;
    request.retryCount = retryCount;

    if (success) {
      this.metrics.successfulRequests++;
      this.metrics.consecutiveFailures = 0;
    } else {
      this.metrics.failedRequests++;
      this.metrics.consecutiveFailures++;
    }

    // Update average response time
    this.updateAverageResponseTime(responseTime);

    // Update error rate
    this.updateErrorRate();

    // Update health status
    this.updateHealthStatus();
  }

  /**
   * Gets current health metrics
   */
  getMetrics(): HealthMetrics {
    return { ...this.metrics };
  }

  /**
   * Gets detailed request history
   */
  getRequestHistory(): RequestMetrics[] {
    return [...this.requestHistory];
  }

  /**
   * Checks if the service is healthy
   */
  isHealthy(): boolean {
    return this.metrics.isHealthy;
  }

  /**
   * Gets health status with details
   */
  getHealthStatus(): {
    isHealthy: boolean;
    status: 'healthy' | 'degraded' | 'unhealthy';
    message: string;
    metrics: HealthMetrics;
  } {
    const { isHealthy, consecutiveFailures, errorRate } = this.metrics;
    
    let status: 'healthy' | 'degraded' | 'unhealthy' = 'healthy';
    let message = 'AI Analysis service is operating normally';

    if (!isHealthy) {
      status = 'unhealthy';
      message = `AI Analysis service is unhealthy: ${consecutiveFailures} consecutive failures, ${Math.round(errorRate * 100)}% error rate`;
    } else if (errorRate > 0.2 || consecutiveFailures > 2) {
      status = 'degraded';
      message = `AI Analysis service is experiencing issues: ${Math.round(errorRate * 100)}% error rate, ${consecutiveFailures} consecutive failures`;
    }

    return {
      isHealthy,
      status,
      message,
      metrics: this.metrics
    };
  }

  /**
   * Resets health metrics
   */
  reset(): void {
    this.metrics = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      averageResponseTime: 0,
      lastRequestTime: 0,
      consecutiveFailures: 0,
      isHealthy: true,
      errorRate: 0
    };
    this.requestHistory = [];
  }

  /**
   * Gets performance recommendations
   */
  getRecommendations(): string[] {
    const recommendations: string[] = [];
    const { errorRate, averageResponseTime, consecutiveFailures } = this.metrics;

    if (errorRate > 0.3) {
      recommendations.push('High error rate detected. Consider checking API key validity and network connectivity.');
    }

    if (averageResponseTime > 30000) {
      recommendations.push('Slow response times detected. Consider using smaller images or checking network speed.');
    }

    if (consecutiveFailures > 3) {
      recommendations.push('Multiple consecutive failures. Consider implementing exponential backoff or circuit breaker pattern.');
    }

    if (this.metrics.totalRequests > 50 && errorRate < 0.1) {
      recommendations.push('Service is performing well. Consider implementing caching for frequently analyzed images.');
    }

    return recommendations;
  }

  private updateAverageResponseTime(responseTime: number): void {
    const totalRequests = this.metrics.successfulRequests + this.metrics.failedRequests;
    if (totalRequests === 1) {
      this.metrics.averageResponseTime = responseTime;
    } else {
      // Calculate rolling average
      this.metrics.averageResponseTime = 
        (this.metrics.averageResponseTime * (totalRequests - 1) + responseTime) / totalRequests;
    }
  }

  private updateErrorRate(): void {
    const totalRequests = this.metrics.successfulRequests + this.metrics.failedRequests;
    if (totalRequests > 0) {
      this.metrics.errorRate = this.metrics.failedRequests / totalRequests;
    }
  }

  private updateHealthStatus(): void {
    const { consecutiveFailures, errorRate } = this.metrics;
    
    this.metrics.isHealthy = 
      consecutiveFailures < this.unhealthyThreshold && 
      errorRate < this.maxErrorRate;
  }
}

// Create singleton instance
export const aiAnalysisHealth = new AIAnalysisHealthMonitor();

/**
 * Decorator function to monitor AI analysis requests
 */
export const withHealthMonitoring = async <T>(
  operation: () => Promise<T>,
  operationName: string = 'AI Analysis'
): Promise<T> => {
  const requestId = aiAnalysisHealth.recordRequestStart();
  let retryCount = 0;
  let lastError: Error | null = null;

  try {
    const result = await operation();
    aiAnalysisHealth.recordRequestEnd(requestId, true, undefined, retryCount);
    return result;
  } catch (error) {
    lastError = error as Error;
    aiAnalysisHealth.recordRequestEnd(requestId, false, lastError.message, retryCount);
    throw error;
  }
};

/**
 * Health check endpoint for monitoring
 */
export const getHealthCheck = () => {
  const healthStatus = aiAnalysisHealth.getHealthStatus();
  const recommendations = aiAnalysisHealth.getRecommendations();
  
  return {
    ...healthStatus,
    recommendations,
    timestamp: new Date().toISOString(),
    uptime: process.uptime ? process.uptime() : 0
  };
};

/**
 * Performance metrics for debugging
 */
export const getPerformanceMetrics = () => {
  const metrics = aiAnalysisHealth.getMetrics();
  const history = aiAnalysisHealth.getRequestHistory();
  
  return {
    metrics,
    recentRequests: history.slice(-10), // Last 10 requests
    timestamp: new Date().toISOString()
  };
};
